import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { StrataLogo } from "@/components/StrataLogo";

interface NaturalLanguageAutomationInputProps {
  onAutomationParsed: (automation: {
    name: string;
    trigger: { type: string; config: any };
    actions: Array<{ type: string; config: any }>;
  }) => void;
}

export function NaturalLanguageAutomationInput({ onAutomationParsed }: NaturalLanguageAutomationInputProps) {
  const [rule, setRule] = useState("");
  const [isParsing, setIsParsing] = useState(false);

  const handleParse = async () => {
    if (!rule.trim()) {
      toast.error("Please enter an automation rule");
      return;
    }

    setIsParsing(true);

    try {
      const { data, error } = await supabase.functions.invoke("parse-automation-rule", {
        body: { rule }
      });

      if (error) throw error;

      if (data.error) {
        if (data.error.includes("Rate limit")) {
          toast.error("AI rate limit reached. Please try again later.");
        } else if (data.error.includes("credits")) {
          toast.error("AI credits exhausted. Please add credits in Settings.");
        } else {
          toast.error(data.error);
        }
        return;
      }

      toast.success(`Automation parsed!`, {
        description: `Confidence: ${Math.round(data.confidence * 100)}%`
      });

      onAutomationParsed({
        name: data.name,
        trigger: data.trigger,
        actions: data.actions
      });

      setRule("");
    } catch (error: any) {
      console.error("Error parsing automation:", error);
      toast.error("Failed to parse automation. Please try again.");
    } finally {
      setIsParsing(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Wand2 className="h-5 w-5 text-primary" />
          Natural Language Automation
        </CardTitle>
        <CardDescription>
          Describe your automation in plain English and let AI build it for you
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Textarea
            placeholder='e.g., "When lead added send WA intro" or "Every day at 9am, send summary email"'
            value={rule}
            onChange={(e) => setRule(e.target.value)}
            rows={3}
            className="resize-none"
          />
          <p className="text-xs text-muted-foreground">
            Examples: "When status changes to closed, email customer" • "When new record created, send webhook to API"
          </p>
        </div>

        <Button
          onClick={handleParse}
          disabled={isParsing || !rule.trim()}
          className="w-full"
        >
          {isParsing ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Parsing...
            </>
          ) : (
            <>
              <StrataLogo size="sm" className="mr-2" />
              Build Automation
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
